import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { CalendarClock, ClipboardCheck, IdCard, Loader2, Plus, Search, Users, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

// Msg 412 — TalentHeart is self-serve: the Client Admin dispatches wellbeing
// surveys AND Personal Profile surveys directly (one / several / department /
// whole company), with an optional deadline. No HM Advisor approval involved.

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(getUiLocale()); } catch { return iso; }
};

function AudiencePicker({
  isES, audience, setAudience, deptId, setDeptId, userIds, setUserIds,
  departments, employees, testIdPrefix,
}) {
  const [empQuery, setEmpQuery] = useState('');
  const filteredEmployees = useMemo(() => {
    const q = empQuery.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      (e.name || '').toLowerCase().includes(q) || (e.position || '').toLowerCase().includes(q));
  }, [employees, empQuery]);

  const toggleUser = (id) => setUserIds((prev) => (
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  ));

  const recipientEstimate = useMemo(() => {
    if (audience === 'all') return employees.length;
    if (audience === 'department') return employees.filter((e) => e.department_id === deptId).length;
    return userIds.length;
  }, [audience, deptId, userIds, employees]);

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <Label>{isES ? 'Destinatarios' : 'Recipients'}</Label>
        <Select value={audience} onValueChange={setAudience}>
          <SelectTrigger data-testid={`${testIdPrefix}-audience`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isES ? 'Toda la empresa' : 'Whole company'}</SelectItem>
            <SelectItem value="department">{isES ? 'Un departamento' : 'A department'}</SelectItem>
            <SelectItem value="employees">{isES ? 'Empleados específicos' : 'Specific employees'}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {audience === 'department' && (
        <div className="space-y-1">
          <Label>{isES ? 'Departamento' : 'Department'}</Label>
          <Select value={deptId || ''} onValueChange={setDeptId}>
            <SelectTrigger data-testid={`${testIdPrefix}-department`}>
              <SelectValue placeholder={isES ? 'Selecciona…' : 'Pick one…'} />
            </SelectTrigger>
            <SelectContent>
              {departments.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name_es || d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {audience === 'employees' && (
        <div className="space-y-1">
          <div className="relative">
            <Search className="h-3 w-3 absolute top-2.5 left-2.5 text-muted-foreground" />
            <Input value={empQuery} onChange={(e) => setEmpQuery(e.target.value)}
                   placeholder={isES ? 'Buscar empleados…' : 'Search employees…'}
                   className="pl-7 h-8 text-xs" data-testid={`${testIdPrefix}-emp-search`} />
          </div>
          <ul className="border rounded-md divide-y max-h-44 overflow-y-auto" data-testid={`${testIdPrefix}-emp-list`}>
            {filteredEmployees.length === 0 ? (
              <li className="px-3 py-3 text-xs text-muted-foreground text-center">
                {isES ? 'Sin empleados' : 'No employees'}
              </li>
            ) : filteredEmployees.map((e) => (
              <li key={e.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-muted/40"
                  onClick={() => toggleUser(e.id)} data-testid={`${testIdPrefix}-emp-${e.id}`}>
                <Checkbox checked={userIds.includes(e.id)} onCheckedChange={() => toggleUser(e.id)} />
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{e.name}</p>
                  {e.position && <p className="text-xs text-muted-foreground truncate">{e.position}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid={`${testIdPrefix}-estimate`}>
        <Users className="h-3 w-3" />
        {isES
          ? `${recipientEstimate} destinatario${recipientEstimate === 1 ? '' : 's'}`
          : `${recipientEstimate} recipient${recipientEstimate === 1 ? '' : 's'}`}
      </p>
    </div>
  );
}

const audienceLabel = (isES, s) => {
  if (s.target_audience === 'department') return isES ? 'Departamento' : 'Department';
  if (s.target_audience === 'employees') return isES ? 'Empleados seleccionados' : 'Selected employees';
  return isES ? 'Toda la empresa' : 'Whole company';
};

export default function WellbeingSurveysDialog({ open, onClose, onChanged }) {
  const { i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');

  const [items, setItems] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [individualInvites, setIndividualInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Wellbeing create form
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [titleEn, setTitleEn] = useState('');
  const [titleEs, setTitleEs] = useState('');
  const [anonymity, setAnonymity] = useState('anonymous');
  const [questionnaireId, setQuestionnaireId] = useState('');
  const [audience, setAudience] = useState('all');
  const [deptId, setDeptId] = useState('');
  const [userIds, setUserIds] = useState([]);
  const [deadline, setDeadline] = useState('');

  // Profile campaign form
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [pAudience, setPAudience] = useState('all');
  const [pDeptId, setPDeptId] = useState('');
  const [pUserIds, setPUserIds] = useState([]);
  const [pDeadline, setPDeadline] = useState('');
  const [sendingProfile, setSendingProfile] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, q, deps, emps, camps] = await Promise.all([
        clientApi.listWellbeingSurveys(),
        clientApi.listWellbeingQuestionnaires().catch(() => ({ items: [] })),
        clientApi.listDepartments().catch(() => []),
        clientApi.listCollaborators().catch(() => []),
        clientApi.listProfileCampaigns().catch(() => ({ items: [] })),
      ]);
      setItems(d?.items || []);
      setQuestionnaires(q?.items || []);
      setDepartments(Array.isArray(deps) ? deps : []);
      setEmployees((Array.isArray(emps) ? emps : []).filter((u) => u.role === 'evaluated'
        && (u.employment_status || 'active') !== 'former'));
      setCampaigns(camps?.items || []);
      setIndividualInvites(camps?.individual_invites || []);
    } catch {
      setItems([]); setQuestionnaires([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open) {
      load();
      setShowCreate(false); setTitleEn(''); setTitleEs('');
      setAnonymity('anonymous'); setQuestionnaireId('');
      setAudience('all'); setDeptId(''); setUserIds([]); setDeadline('');
      setShowProfileForm(false); setPAudience('all'); setPDeptId(''); setPUserIds([]); setPDeadline('');
    }
  }, [open, load]);

  const sorted = useMemo(() => {
    const rank = { active: 0, draft: 1, closed: 2, archived: 3 };
    return [...items].sort((a, b) =>
      (rank[a.status] ?? 9) - (rank[b.status] ?? 9)
      || (b.created_at || '').localeCompare(a.created_at || ''),
    );
  }, [items]);

  const validateAudience = (aud, dep, uids) => {
    if (aud === 'department' && !dep) {
      toast.error(isES ? 'Selecciona un departamento.' : 'Pick a department.');
      return false;
    }
    if (aud === 'employees' && uids.length === 0) {
      toast.error(isES ? 'Selecciona al menos un empleado.' : 'Select at least one employee.');
      return false;
    }
    return true;
  };

  const create = async () => {
    if (titleEn.trim().length < 2 || titleEs.trim().length < 2) {
      toast.error(isES ? 'Completa ambos títulos.' : 'Both titles are required.');
      return;
    }
    if (!questionnaireId) {
      toast.error(isES
        ? 'Selecciona un cuestionario publicado de TalentHeart.'
        : 'Select a published TalentHeart questionnaire.');
      return;
    }
    if (!validateAudience(audience, deptId, userIds)) return;
    setCreating(true);
    try {
      await clientApi.createWellbeingSurvey({
        title_en: titleEn.trim(), title_es: titleEs.trim(),
        anonymity,
        target_audience: audience,
        target_department_id: audience === 'department' ? deptId : null,
        target_user_ids: audience === 'employees' ? userIds : [],
        deadline: deadline || null,
        questionnaire_id: questionnaireId,
      });
      toast.success(isES ? 'Encuesta creada en borrador' : 'Survey created as draft', {
        description: isES ? 'Ábrela para enviarla a los destinatarios.' : 'Open it to send it to recipients.',
      });
      setShowCreate(false); setTitleEn(''); setTitleEs(''); setQuestionnaireId('');
      setAudience('all'); setDeptId(''); setUserIds([]); setDeadline('');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(isES ? 'No se pudo crear' : 'Could not create', { description: formatApiError(e) });
    } finally { setCreating(false); }
  };

  const sendProfileCampaign = async () => {
    if (!validateAudience(pAudience, pDeptId, pUserIds)) return;
    setSendingProfile(true);
    try {
      const res = await clientApi.createProfileCampaign({
        target_audience: pAudience,
        target_department_id: pAudience === 'department' ? pDeptId : null,
        target_user_ids: pAudience === 'employees' ? pUserIds : [],
        deadline: pDeadline || null,
      });
      toast.success(
        isES
          ? `Encuesta de perfil enviada a ${res.invited} empleado${res.invited === 1 ? '' : 's'}`
          : `Profile survey sent to ${res.invited} employee${res.invited === 1 ? '' : 's'}`,
        res.already_completed
          ? { description: isES
              ? `${res.already_completed} ya tenían su perfil completo.`
              : `${res.already_completed} already had a completed profile.` }
          : undefined,
      );
      setShowProfileForm(false); setPAudience('all'); setPDeptId(''); setPUserIds([]); setPDeadline('');
      await load();
      onChanged?.();
    } catch (e) {
      const notPublished = e?.response?.status === 422
        && e?.response?.data?.detail === 'questionnaire_not_published';
      toast.error(isES ? 'No se pudo enviar' : 'Could not send', {
        description: notPublished
          ? (isES
            ? 'El cuestionario de perfil aún no está publicado por CTH. Las invitaciones se habilitarán cuando exista una versión publicada.'
            : 'The profile questionnaire has not been published by CTH yet. Invitations will be enabled once a published version exists.')
          : formatApiError(e),
      });
    } finally { setSendingProfile(false); }
  };

  const transition = async (s, fn, label) => {
    setBusyId(s.id);
    try {
      await fn(s.id);
      toast.success(label);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error('Error', { description: formatApiError(e) });
    } finally { setBusyId(null); }
  };

  const statusBadge = (status) => {
    const colors = {
      active: 'bg-success/15 text-success border-success/30',
      draft: 'bg-muted text-muted-foreground border-border',
      closed: 'bg-info/15 text-info border-info/30',
      archived: 'bg-muted text-muted-foreground border-border',
    };
    const labels = isES
      ? { active: 'Activa', draft: 'Borrador', closed: 'Cerrada', archived: 'Archivada' }
      : { active: 'Active', draft: 'Draft', closed: 'Closed', archived: 'Archived' };
    return <Badge variant="outline" className={colors[status] || ''}>{labels[status] || status}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="surveys-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-primary" />
            {isES ? 'Encuestas TalentHeart' : 'TalentHeart surveys'}
          </DialogTitle>
          <DialogDescription>
            {isES
              ? 'Envía encuestas de bienestar y de perfil personal directamente a tus colaboradores — sin aprobaciones intermedias.'
              : 'Send wellbeing and personal-profile surveys directly to your people — no intermediate approvals.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="wellbeing">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="wellbeing" data-testid="surveys-tab-wellbeing">
              {isES ? 'Bienestar' : 'Wellbeing'}
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="surveys-tab-profile">
              {isES ? 'Perfil personal' : 'Personal profile'}
            </TabsTrigger>
          </TabsList>

          {/* ------------------- Wellbeing surveys tab ------------------- */}
          <TabsContent value="wellbeing" className="mt-3 space-y-3">
            {showCreate ? (
              <div className="space-y-3 border rounded-md p-3 bg-muted/30" data-testid="surveys-create-form">
                <div className="space-y-1">
                  <Label>{isES ? 'Cuestionario (Biblioteca de Evaluaciones)' : 'Questionnaire (Evaluation Library)'}</Label>
                  {questionnaires.length === 0 ? (
                    <p className="text-xs text-destructive border border-destructive/30 bg-destructive/5 rounded px-2 py-1.5"
                       data-testid="surveys-create-no-questionnaires">
                      {isES
                        ? 'No hay cuestionarios de TalentHeart publicados. Pide al equipo Corporate Talent Hub que publique uno en la Biblioteca de Evaluaciones.'
                        : 'No published TalentHeart questionnaires available. Ask the Corporate Talent Hub team to publish one in the Evaluation Library.'}
                    </p>
                  ) : (
                    <Select value={questionnaireId} onValueChange={setQuestionnaireId}>
                      <SelectTrigger data-testid="surveys-create-questionnaire">
                        <SelectValue placeholder={isES ? 'Selecciona un cuestionario…' : 'Pick a questionnaire…'} />
                      </SelectTrigger>
                      <SelectContent>
                        {questionnaires.map((q) => (
                          <SelectItem key={q.id} value={q.id} data-testid={`surveys-create-questionnaire-${q.code || q.id}`}>
                            {q.name} {q.questions_count ? `· ${q.questions_count} ${isES ? 'preguntas' : 'questions'}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>{isES ? 'Título (EN)' : 'Title (EN)'}</Label>
                    <Input value={titleEn} onChange={(e) => setTitleEn(e.target.value)} placeholder="Q2 Wellbeing Pulse" data-testid="surveys-create-title-en" />
                  </div>
                  <div className="space-y-1">
                    <Label>{isES ? 'Título (ES)' : 'Title (ES)'}</Label>
                    <Input value={titleEs} onChange={(e) => setTitleEs(e.target.value)} placeholder="Pulso Q2" data-testid="surveys-create-title-es" />
                  </div>
                </div>
                <AudiencePicker
                  isES={isES}
                  audience={audience} setAudience={setAudience}
                  deptId={deptId} setDeptId={setDeptId}
                  userIds={userIds} setUserIds={setUserIds}
                  departments={departments} employees={employees}
                  testIdPrefix="surveys-create"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>{isES ? 'Anonimato' : 'Anonymity'}</Label>
                    <Select value={anonymity} onValueChange={setAnonymity}>
                      <SelectTrigger data-testid="surveys-create-anonymity"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="anonymous">{isES ? 'Anónima (recomendado)' : 'Anonymous (recommended)'}</SelectItem>
                        <SelectItem value="named">{isES ? 'Identificada' : 'Named'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>{isES ? 'Fecha límite (opcional)' : 'Deadline (optional)'}</Label>
                    <Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)}
                           lang={i18n.language === 'es' ? 'es-PA' : 'en-US'}
                           data-testid="surveys-create-deadline" />
                    <p className="text-xs text-muted-foreground mt-1">
                      {isES ? 'Formato: dd/mm/aaaa · Hora de Panamá' : 'Format: mm/dd/yyyy · Panama time'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)} disabled={creating} data-testid="surveys-create-cancel">
                    {isES ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button size="sm" onClick={create}
                          disabled={creating || questionnaires.length === 0}
                          data-testid="surveys-create-submit">
                    {creating && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                    {isES ? 'Crear borrador' : 'Create draft'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-end">
                <Button size="sm" variant="outline" onClick={() => setShowCreate(true)} data-testid="surveys-new-cta">
                  <Plus className="h-3 w-3 mr-1" />
                  {isES ? 'Nueva encuesta' : 'New survey'}
                </Button>
              </div>
            )}

            {loading ? (
              <div className="flex items-center text-sm text-muted-foreground py-6">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {isES ? 'Cargando…' : 'Loading…'}
              </div>
            ) : sorted.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="surveys-empty">
                {isES ? 'Aún no hay encuestas. Crea la primera.' : 'No surveys yet. Create the first one.'}
              </p>
            ) : (
              <ul className="space-y-2" data-testid="surveys-list">
                {sorted.map((s) => (
                  <li key={s.id} className="border rounded-md p-3 space-y-2" data-testid={`survey-${s.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate">{isES ? s.title_es : s.title_en}</p>
                      {statusBadge(s.status)}
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span>
                        {(s.questions || []).length} {isES ? 'preguntas' : 'questions'} · {(s.response_count || 0)} {isES ? 'respuestas' : 'responses'} · {audienceLabel(isES, s)}
                        {s.deadline && (
                          <> · <CalendarClock className="h-3 w-3 inline -mt-0.5" /> {fmtDate(s.deadline)}</>
                        )}
                      </span>
                      <div className="flex gap-1">
                        {s.status === 'draft' && (
                          <Button size="sm" variant="default" className="h-6 px-2 text-xs"
                            onClick={() => transition(s, clientApi.openWellbeingSurvey, isES ? 'Encuesta abierta y enviada' : 'Survey opened & sent')}
                            disabled={busyId === s.id}
                            data-testid={`survey-${s.id}-open`}>
                            {isES ? 'Abrir' : 'Open'}
                          </Button>
                        )}
                        {s.status === 'active' && (
                          <Button size="sm" variant="outline" className="h-6 px-2 text-xs"
                            onClick={() => transition(s, clientApi.closeWellbeingSurvey, isES ? 'Encuesta cerrada' : 'Survey closed')}
                            disabled={busyId === s.id}
                            data-testid={`survey-${s.id}-close`}>
                            {isES ? 'Cerrar' : 'Close'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          {/* ----------------- Personal profile survey tab ----------------- */}
          <TabsContent value="profile" className="mt-3 space-y-3">
            <p className="text-xs text-muted-foreground">
              {isES
                ? 'Invita a tus colaboradores a completar su perfil personal voluntario: intereses, hobbies, preferencias y cumpleaños. Los resultados alimentan las celebraciones y el conocimiento del equipo.'
                : 'Invite your people to complete their voluntary personal profile: interests, hobbies, preferences and birthdays. Results feed celebrations and team insights.'}
            </p>
            {showProfileForm ? (
              <div className="space-y-3 border rounded-md p-3 bg-muted/30" data-testid="profile-campaign-form">
                <AudiencePicker
                  isES={isES}
                  audience={pAudience} setAudience={setPAudience}
                  deptId={pDeptId} setDeptId={setPDeptId}
                  userIds={pUserIds} setUserIds={setPUserIds}
                  departments={departments} employees={employees}
                  testIdPrefix="profile-campaign"
                />
                <div className="space-y-1">
                  <Label>{isES ? 'Fecha límite (opcional)' : 'Deadline (optional)'}</Label>
                  <Input type="date" value={pDeadline} onChange={(e) => setPDeadline(e.target.value)}
                         data-testid="profile-campaign-deadline" />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setShowProfileForm(false)} disabled={sendingProfile}
                          data-testid="profile-campaign-cancel">
                    {isES ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button size="sm" onClick={sendProfileCampaign} disabled={sendingProfile}
                          data-testid="profile-campaign-send">
                    {sendingProfile && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                    {isES ? 'Enviar ahora' : 'Send now'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-end">
                <Button size="sm" variant="outline" onClick={() => setShowProfileForm(true)} data-testid="profile-campaign-new-cta">
                  <IdCard className="h-3 w-3 mr-1" />
                  {isES ? 'Enviar encuesta de perfil' : 'Send profile survey'}
                </Button>
              </div>
            )}

            {loading ? (
              <div className="flex items-center text-sm text-muted-foreground py-6">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {isES ? 'Cargando…' : 'Loading…'}
              </div>
            ) : (campaigns.length === 0 && individualInvites.length === 0) ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="profile-campaigns-empty">
                {isES ? 'Aún no has enviado encuestas de perfil ni solicitudes individuales.' : 'No profile surveys or individual requests sent yet.'}
              </p>
            ) : (
              <div className="space-y-3">
              {campaigns.length > 0 && (
              <ul className="space-y-2" data-testid="profile-campaigns-list">
                {campaigns.map((c) => (
                  <li key={c.id} className="border rounded-md p-3" data-testid={`profile-campaign-${c.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium">
                        {audienceLabel(isES, c)}
                        <span className="text-xs text-muted-foreground font-normal ml-2">
                          {fmtDate(c.created_at)}
                        </span>
                      </p>
                      <Badge variant="outline" className="text-xs">
                        {c.completed_count}/{c.invited_count} {isES ? 'completados' : 'completed'}
                      </Badge>
                    </div>
                    {c.deadline && (
                      <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">
                        <CalendarClock className="h-3 w-3" />
                        {isES ? 'Fecha límite' : 'Deadline'}: {fmtDate(c.deadline)}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
              )}
              {individualInvites.length > 0 && (
                <div className="space-y-2" data-testid="profile-individual-invites">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {isES ? 'Solicitudes individuales (acción de perfil)' : 'Individual requests (profile action)'}
                  </p>
                  <ul className="space-y-2">
                    {individualInvites.map((inv) => {
                      const stCls = inv.status === 'completed'
                        ? 'bg-success/15 text-success border-success/30'
                        : inv.status === 'pending'
                          ? 'bg-warning/15 text-warning border-warning/30'
                          : 'bg-destructive/10 text-destructive border-destructive/30';
                      const stLabel = inv.status === 'completed'
                        ? (isES ? 'Completada' : 'Completed')
                        : inv.status === 'pending'
                          ? (isES ? 'Pendiente' : 'Pending')
                          : inv.status === 'invalid_unpublished'
                            ? (isES ? 'No válida — cuestionario no publicado' : 'Invalid — questionnaire not published')
                            : inv.status;
                      return (
                        <li key={inv.id} className="border rounded-md p-3 flex items-center justify-between gap-2"
                            data-testid={`profile-invite-${inv.id}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{inv.user_name || '—'}</p>
                            <p className="text-xs text-muted-foreground">{fmtDate(inv.sent_at)}</p>
                          </div>
                          <Badge variant="outline" className={`text-xs ${stCls}`} data-testid={`profile-invite-status-${inv.id}`}>
                            {stLabel}
                          </Badge>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="surveys-close-dialog">
            <X className="h-3 w-3 mr-1" />
            {isES ? 'Cerrar' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
